<template lang="pug">
    v-layout.justify-content(row wrap)
        Navbar(noBackChevron noMenuIcon)
        // Loader
        v-flex(v-show="loading")
            FanLoader( :loading="loading")
            div(slot="loadingMsg"
                class="full-width text-xs-center headline my-2"
            ) Loading...
        
        // Payment Form View
        responsive-flex(v-show="!loading && clearentSDKInitiated")

            // Payment Header and Information
            h1#payment-header-info.mt-5 {{ paymentData.companyName }}
            span( v-html="poweredLogo" )

            div.pay-info(style="font-size: 20px;").mt-5
                p#payment-amount #[strong Amount: ] ${{ paymentAmount }}
                p#payment-description #[strong Description: ] {{ paymentData.description }}

                div(v-if="hasError" class="errorMsg")
                    span( v-html="errorMessage" )

                // Clearent Payment SDK
                div#payment-form
                    
                v-text-field(
                    id="payment-zipcode"
                    v-model="zipCode"
                    outline 
                    label="Zip Code"
                    :rules="[rules.required, rules.zipCode]"
                )

                v-text-field#payment-email(
                    v-if="!paymentData.lastSentToEmail" 
                    v-model="email"
                    outline 
                    label="Where should we send your receipt?"
                    :rules="[rules.required, rules.email]"
                )

            // Credit Card Transaction Button
            v-btn#submit-btn(class="primary" @click="submitPayment()" :disabled="submitDisabled") 
                v-icon.pr-1 mdi-lock
                | Submit Payment

        // Payment Confirmation
        responsive-flex.mt-5(v-show="!loading && paymentConfirmed")
            img(:src="saveCheck" style="width: 200px")          
            .headline#payment-confirmation-headline Your payment was successful!

            v-list.mt-5.mx-5.text-xs-left
                | #[strong Transaction ID:] {{ transactionInfo ? transactionInfo.transactionId : null }}
                v-divider
                | #[strong Amount:] ${{ paymentAmount }}
                v-divider
                | #[strong To:] {{ paymentData.companyName }}
                v-divider
                | #[strong From:] {{ paymentData.customerName  }}
                v-divider
                | #[strong Transaction Type:] {{ transactionType }}
                v-divider
                | #[strong Last Four Digits of Payment:] {{ lastFour }}
                v-divider
                | #[strong Description:] {{ paymentData.description }}
                v-divider

            .subtitle.px-2.mt-2 Please check #[strong {{ emailReceipt }} ] for an emailed receipt.
            .px-2.mt-2 #[strong {{ paymentData.companyName }}] will reach out to you shortly. You can now close this window to clear any sensitive data.
            
            // Typeform Payment Feedback Survey
            .mt-3
                a(flat color="primary" :href="paymentSurvey" target="_blank") Share Feedback
                    v-icon(color="primary").pl-1 mdi-open-in-new

</template>

<script>
    import FanLoader from 'Components/FanLoader'
    import Navbar from 'Components/Navbar.vue'
    import ResponsiveFlex from 'Components/ResponsiveFlex.vue'
    import { clearent } from '@/helpers/integrations'
    import { delayMilliseconds } from 'Helpers/utilFunctions.js'
    import { numberWithCommas } from "@/helpers/parsingFunctions"
    import { openClearentPaymentLink, getClearentMobileTerminal, clearentCreditCardTransaction, clearentAchTransaction } from '@/helpers/c4cAPIClient'
    import { logger } from 'Helpers/logger.js'
    import { mapActions } from 'vuex'
    import envVars from '@/env_vars.js'

    const { logoSvg } = clearent

    export default {
        metaInfo: {
            script: [
                // Clearent Payment SDK
                { src: envVars.clearentSDKUrl, async: true, defer: true }
            ],
        },
        data: () => ({
            email: null,
            lastFour: '',
            loading: true,
            paymentData: {},
            paymentTokenData: {},
            clearentSDKData: {},
            clearentSDKInitiated: null,
            paymentSurvey: "https://prod-research.typeform.com/payel-consumer",
            paymentConfirmed: null,
            transactionInfo: null,
            saveCheck: require('@/assets/successgif.gif'),
            zipCode: null,
            rules: {
                email: value => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value) || 'Invalid email',
                required: v => !!v || 'Required.',
                zipCode: v => /^[0-9]{5}([- ]?[0-9]{4})?$/.test(v) || 'Invalid zip code'
            },
            hasError: false,
            errorMessage: '',
        }),
        components: {
            FanLoader,
            Navbar,
            ResponsiveFlex
        }, 
        computed: {
            emailReceipt() {
                return this.paymentData.lastSentToEmail ? this.paymentData.lastSentToEmail : this.email
            },
            poweredLogo() {
                return `Powered by: <img src=${logoSvg} style="width: 100px; vertical-align: middle;">`
            },
            paymentAmount() {
                return this.paymentData.amount ? numberWithCommas(this.paymentData.amount.toFixed(2)) : null
            },
            submitDisabled() {
                return false
            },
            transactionType() {
                return this.paymentTokenData.payloadType === 'ach-jwt' ? 'Ach Transaction' : 'Debit/Credit Transaction'
            },
        },
        async mounted() {
            // Step 1, run openlink Endpoint
            try {
                const res = await openClearentPaymentLink(this.$route.params.linkId) //access token and all data with easypay link
                this.paymentData = res.data

                // Step 2, Grab Access Token and Set as EasyPayAccessToken
                await this.setEasyPayAccessToken(this.paymentData.accessToken) // this is the access token to get access to the public state.

                // Step 3, Get Clearent Mobile Terminal
                const results = await getClearentMobileTerminal(this.paymentData.dealerId)
                this.clearentSDKData = results.data

                // Step 4, Grab Public Key & Use Public Key for Clearent SDK Init
                ClearentSDK.init({
                    "baseUrl": this.clearentSDKData.gatewayBaseUrl,
                    "pk": this.clearentSDKData.publicKey,
                    "enableAch": this.clearentSDKData.achEnabled,
                    "showValidationMessages": false,
                    "styles": ".transaction-form{padding-bottom: 10px;} #card-group.has-error, #exp-date-group.has-error, #cvc-group.has-error, #account-type-group.has-error, #individual-name-group.has-error, #routing-number-group.has-error, #account-number-group.has-error{ border: 1px solid #dc143c}}"
                })
                await delayMilliseconds(3000)
                this.clearentSDKInitiated = true
                this.loading = false
            } catch (error) {
                this.error(error.response.data.message)
            }
        },
        methods: {
            ...mapActions([
                'setEasyPayAccessToken', 'error'
            ]),
            emailCheck(msg) {
                return v => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                .test(v) || msg || 'Invalid email'
            },
            zipCodeCheck(msg) {
                return v => /^[0-9]{5}([- ]?[0-9]{4})?$/.test(v) || msg || 'Invalid zip code'
            },
            isEmailValid() {
                const currentEmail = this.paymentData.lastSentToEmail ? this.paymentData.lastSentToEmail : this.email
                const emailChecked = this.emailCheck()(currentEmail)
                if(emailChecked !== true){
                    this.hasError = true
                    this.errorMessage = 'Email is Invalid'
                    this.clearentSDKInitiated = true
                    return false
                }
                return true
            },
            isZipCodeValid() {
                const zipCodeChecked = this.zipCodeCheck()(this.zipCode)
                if(zipCodeChecked !== true){
                    this.hasError = true
                    this.errorMessage = 'Zip Code is Invalid'
                    this.loading = false
                    this.clearentSDKInitiated = true
                    return false
                }
                return true
            },
            prepareErrorMsg(string) {
                // gets the error message and checks how many fields are required.
                const occurrences = string.toLowerCase().split('required').length - 1;
                if(occurrences > 2){
                    return 'All fields are Required';
                } else {
                    return string;
                }
            },
            async submitPayment() {
                this.clearentSDKInitiated = false
                this.hasError = false
                try {
                    if(!this.isEmailValid()) return
                    const res = await ClearentSDK.getPaymentToken();
                    this.paymentTokenData = res.payload
                    this.loading = true
                    try {
                        if (this.paymentTokenData.payloadType === "ach-jwt") {
                            this.lastFour = this.paymentTokenData["ach-jwt"]["account-number-last-four"]
                            const res = await clearentAchTransaction({
                                dealerId: this.paymentData.dealerId,
                                amount: this.paymentData.amount,
                                achMobileJwt: this.paymentTokenData["ach-jwt"].jwt,
                                receiptEmail: this.paymentData.lastSentToEmail ? this.paymentData.lastSentToEmail : this.email,
                                description: this.paymentData.description,
                            })
                            this.transactionInfo = res.data
                            console.log('ACH SUCCESS')
                            logger(this.transactionInfo, `ACH payment success`)
                        } else {
                            if(!this.isZipCodeValid()) return
                            this.lastFour = this.paymentTokenData["mobile-jwt"]["last-four"]
                            const res = await clearentCreditCardTransaction({
                                dealerId: this.paymentData.dealerId,
                                amount: this.paymentData.amount,
                                mobileJwt: this.paymentTokenData["mobile-jwt"].jwt,
                                receiptEmail: this.paymentData.lastSentToEmail ? this.paymentData.lastSentToEmail : this.email,
                                postalCode: this.zipCode,
                                description: this.paymentData.description,
                            })
                            this.transactionInfo = res.data
                            console.log('CREDIT SUCCESS')
                            logger(this.transactionInfo, `Credit/Debit Card payment success`)
                        }
                        this.paymentConfirmed = true
                        this.loading = false
                    } catch (error) {
                        if (this.paymentTokenData.payloadType === "ach-jwt"){
                            console.log("Submitting ACH Transaction has failed" , error)
                        } else {
                            console.log("Submitting Card Transaction has failed" , error)
                        }
                        this.hasError = true
                        this.errorMessage = `${error.response.data.message} <br/>Please check your account information`
                        this.clearentSDKInitiated = true
                        this.loading = false
                        logger(error, `Error with our transaction endpoints: ${error.response.data.message}`)
                    }
                } catch(error) {
                    console.log('Error with Clearent Payment SDK', error.payload)
                    this.hasError = true
                    this.errorMessage = this.prepareErrorMsg(error.payload.error["error-message"])
                    this.clearentSDKInitiated = true
                    // logger(error, `Error getting Clearent Token: ${error.payload.error["error-message"]}`)
                }
            }
        },
    }
</script>

<style lang="scss" scoped>

    #payment-form {
        margin: 10px 0px;
    }

    #has-error {
        color: red !important;
    }

    .v-text-field--outline::v-deep .v-input__slot {
        border: 1px solid lightgray !important;
    }

    .pay-info {
        margin: 5%;
        text-align: left;
    }

    .errorMsg {
        color: red;
        font-size: 1rem;
        margin-top: 10px;
    }
</style>